'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { UserProfile } from '@/lib/database.types';

interface AuthContextValue {
  user: any | null;
  session: any | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const supabase = createClient();

  const fetchProfile = async (userId: string, userEmail?: string, userMeta?: any) => {
    setProfileLoading(true);
    try {
      // Try by id first (preferred — matches get_profile_id() DB function)
      let { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Fallback: try by user_id column
      if (!data && !error) {
        const fallback = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        data = fallback.data;
        error = fallback.error;
      }

      if (!error && data) {
        setProfile(data as UserProfile);
        return;
      }

      // Profile not found — auto-create it with role = user
      const fullName = userMeta?.full_name || userMeta?.fullName || (userEmail ? userEmail.split('@')[0] : 'User');
      const email = userEmail || '';

      const { data: created, error: createError } = await supabase
        .from('user_profiles')
        .upsert({
          id: userId,
          user_id: userId,
          full_name: fullName,
          email: email,
          role: 'user',
        }, { onConflict: 'id' })
        .select()
        .single();

      if (createError) {
        console.error('[AuthContext] Failed to auto-create profile:', createError);
      } else {
        setProfile(created as UserProfile);
      }
    } catch (err) {
      console.error('[AuthContext] fetchProfile error:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.user.email, session.user.user_metadata);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.user.email, session.user.user_metadata);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          // role is intentionally NOT passed — DB/profile creation always sets role = 'user'
        },
      },
    });

    if (error) throw error;

    // Create/upsert profile with role = user
    if (data?.user) {
      const userId = data.user.id;
      const fullName = metadata?.fullName || metadata?.full_name || email.split('@')[0];
      await supabase.from('user_profiles').upsert({
        id: userId,
        user_id: userId,
        full_name: fullName,
        email: email,
        role: 'user',
      }, { onConflict: 'id' });
    }

    // Sign out immediately so the user must log in manually
    await supabase.auth.signOut();

    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Always returns true — email verification is not required
  const isEmailVerified = () => {
    return true;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    let { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (!data && !error) {
      const fallback = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      data = fallback.data;
      error = fallback.error;
    }
    if (error) throw error;
    return data as UserProfile | null;
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id, user.email, user.user_metadata);
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
